import {
  isDefined,
  isEmpty,
  isNotDefined,
  isNotEmpty,
  PERMISSION_DEFINITION,
  searchIncludes,
  sortPermissionOptions,
  type PermissionDefinition,
  type PermissionEntityId,
  type PermissionEntityScope,
  type PermissionList,
  type PermissionListItem,
  type PermissionType,
  type ScopeWithSource,
} from '@terros/common'
import { permissionsRecord, type GroupedPermissionDefinitions, type PermissionDefinitionGroup } from '@front/model'
import { type PermissionAllowedScope, type PermissionEditScope } from './types'

export const getPermissionActionDescription = (definition: PermissionDefinition): string =>
  `Allow the user to ${definition.action}${definition.additionalDescription ?? ''}`

export const stringifyPermissionScopes = <T extends PermissionAllowedScope>(scopes: T | T[]): string => {
  const arr = Array.isArray(scopes) ? scopes : [scopes]
  return arr.map((scope) => stringifyPermissionScope(scope)).join(' and ')
}

export const stringifyPermissionScope = (scope: PermissionAllowedScope): string => {
  switch (scope) {
    case 'none':
      return 'disabled'
    case 'company':
      return 'the company'
    case 'downline':
      return 'their team'
    case 'self':
      return 'themselves'
    case 'withinRestrictions':
      return 'all users and teams within visibility restrictions'
    default:
      return scope
  }
}

export const getRadioOptionProps = (
  optionEditInfo: PermissionListItem,
  entity: PermissionEditScope
): { disabled: boolean; checked: boolean; isDefault: boolean } => {
  const { overridable, primaryScope } = optionEditInfo
  const checked = primaryScope?.entity === entity
  const disabled = !overridable.some((s) => s === entity)
  return { disabled, checked, isDefault: false }
}

type RadioGroupProps = {
  canEdit: boolean
  isPermEnabled: boolean
  isSwitchToggleable: boolean
  switchOnly: boolean
  availableEntities: PermissionAllowedScope[]
}

export const getRadioGroupProps = ({ overridable, primaryScope }: PermissionListItem): RadioGroupProps => {
  const displayScopes = overridable.filter((e) => e !== 'none')

  const current = primaryScope?.entity ?? 'none'

  if (current !== 'none' && !displayScopes.includes(current)) {
    displayScopes.push(current)
  }

  const availableEntities: PermissionAllowedScope[] = sortPermissionOptions(displayScopes)

  const canEdit = isNotEmpty(overridable)
  const isPermEnabled = current !== 'none'
  const isSwitchToggleable =
    current === 'none' ? isNotEmpty(overridable.filter((e) => e !== 'none')) : overridable.includes('none')
  const switchOnly = availableEntities.filter((e) => e !== 'none').length <= 1

  return { canEdit, isPermEnabled, isSwitchToggleable, switchOnly, availableEntities }
}

export type PermissionFilter = {
  isWorkflowEnabled: boolean
  isRecorderEnabled: boolean
  isAIEnabled: boolean
  isTantalim: boolean
}
export const buildDisplayPermissionsRecord = (
  permissionList: PermissionList,
  filter: PermissionFilter
): GroupedPermissionDefinitions => {
  return Array.from(permissionsRecord.entries()).reduce<GroupedPermissionDefinitions>(
    (acc, [category, definitions]) => {
      const filtered = Array.from(definitions.entries()).filter(([type]) => {
        if (!isDefined(permissionList[type])) return false
        if (shouldFilterPermission(type, filter)) return false
        return true
      })
      if (isNotEmpty(filtered)) {
        acc.set(category, new Map(filtered))
      }
      return acc
    },
    new Map<string, PermissionDefinitionGroup>()
  )
}

export const shouldFilterPermission = (
  type: PermissionType,
  { isWorkflowEnabled, isRecorderEnabled, isAIEnabled, isTantalim }: PermissionFilter
): boolean => {
  const definition: PermissionDefinition = PERMISSION_DEFINITION[type]
  if (definition.internal && !isTantalim) return true
  if (definition.featureId === 'Feature:TerrosAIChat' && !isAIEnabled) return true

  const shouldHide = ({
    isEnabled,
    enabledTypes,
    disabledTypes,
  }: {
    isEnabled: boolean
    enabledTypes: PermissionType[]
    disabledTypes: PermissionType[]
  }): boolean => {
    return isEnabled ? disabledTypes.includes(type) : enabledTypes.includes(type)
  }

  return (
    shouldHide({
      isEnabled: isWorkflowEnabled,
      enabledTypes: WORKFLOW_ENABLED_PERMISSIONS,
      disabledTypes: WORKFLOW_DISABLED_PERMISSIONS,
    }) ||
    shouldHide({
      isEnabled: isRecorderEnabled,
      enabledTypes: RECORDER_ENABLED_PERMISSIONS,
      disabledTypes: RECORDER_DISABLED_PERMISSIONS,
    })
  )
}

const WORKFLOW_ENABLED_PERMISSIONS: PermissionType[] = ['workflow:list', 'workflow:manage', 'tag:manage']
const WORKFLOW_DISABLED_PERMISSIONS: PermissionType[] = [
  'contact:add',
  'contact:delete',
  'contact:import',
  'contact:list',
  'disposition:save',
  'accountStatus:list',
  'accountStatus:save',
  'event:outcome:list',
  'event:outcome:save',
]

const RECORDER_ENABLED_PERMISSIONS: PermissionType[] = ['conversation:manage', 'conversation:list', 'compliance:manage']
const RECORDER_DISABLED_PERMISSIONS: PermissionType[] = [
  'evaluation:add',
  'evaluation:list',
  'evaluation:delete',
  'pitch:record',
  'pitch:delete',
]

export const filterPermissionsRecord = (
  record: GroupedPermissionDefinitions,
  query: string
): GroupedPermissionDefinitions => {
  if (!query) return record
  return Array.from(record.entries()).reduce<GroupedPermissionDefinitions>((acc, [category, definitions]) => {
    const filteredDefinitions = Array.from(definitions.entries()).filter(([type, definition]) => {
      const compareList = [definition.label, definition.action, type, definition.additionalDescription ?? '']
      return searchIncludes(compareList, query)
    })
    if (isNotEmpty(filteredDefinitions)) {
      acc.set(category, new Map(filteredDefinitions))
    }
    return acc
  }, new Map<string, PermissionDefinitionGroup>())
}

export const LEGACY_PERMISSION_GROUP_QUERY_KEY = 'currentGroup'

export const CHANGED_PERMISSION_GROUP_KEY = 'permission:changed'

export const permissionGroupQueryKey = (entityId: PermissionEntityId): string => `permissionGroupFor${entityId}`

export const requestedPermissionGroups = ({
  selected,
  legacy,
  fallback,
}: {
  selected: string[] | null
  legacy: string | null
  fallback: string | undefined
}): string[] | null => {
  if (isDefined(selected)) return selected
  const single = legacy ?? fallback
  return isDefined(single) ? [single] : null
}

export const requestedCategoryGroups = (requested: string[] | null): string[] | null => {
  if (isNotDefined(requested)) return requested
  return requested.filter((group) => group !== CHANGED_PERMISSION_GROUP_KEY)
}

export const resolvePermissionGroups = (categories: string[], requested: string[] | null | undefined): string[] => {
  const stillAvailable = (requested ?? []).filter((category) => categories.includes(category))
  if (isNotEmpty(stillAvailable)) return stillAvailable
  if (isEmpty(requested)) return []
  return categories.slice(0, 1)
}

export const resolvePermissionGroup = (
  categories: string[],
  requested: string[] | null | undefined
): string | undefined => resolvePermissionGroups(categories, requested).at(0) ?? categories.at(0)

export const getInheritedScope = (scopes: ScopeWithSource[] | undefined): PermissionAllowedScope => {
  if (isEmpty(scopes)) return 'none'
  const entities = scopes.map((s) => s.entity)
  const sorted = sortPermissionOptions(entities)
  return sorted.at(0) ?? 'none'
}

export type ScopeEditorProps = {
  availableScopes: PermissionEntityScope[]
  currentScope: PermissionEditScope
}

export const getScopeEditorProps = (
  info: PermissionListItem | undefined,
  entityId: PermissionEntityId
): ScopeEditorProps => {
  const overrideScope = info?.allOverrides?.find((override) => override.entityId === entityId)?.scope
  return {
    availableScopes: sortPermissionOptions([...(info?.overridable ?? [])]),
    currentScope: !overrideScope || overrideScope === 'inherit' ? 'default' : overrideScope,
  }
}

export const isPermissionChanged = ({ primaryScope }: PermissionListItem, entityId: PermissionEntityId): boolean =>
  primaryScope?.source === entityId

export const filterChangedPermissionsRecord = (
  groups: GroupedPermissionDefinitions,
  permissionList: PermissionList,
  entityId: PermissionEntityId
): GroupedPermissionDefinitions =>
  Array.from(groups.entries()).reduce<GroupedPermissionDefinitions>((acc, [category, definitions]) => {
    const changed = Array.from(definitions.entries()).filter(([type]) => {
      const info = permissionList[type]
      return isDefined(info) && isPermissionChanged(info, entityId)
    })
    if (isNotEmpty(changed)) {
      acc.set(category, new Map(changed))
    }
    return acc
  }, new Map<string, PermissionDefinitionGroup>())

export const changedPermissionDefinitions = (
  groups: GroupedPermissionDefinitions,
  permissionList: PermissionList,
  entityId: PermissionEntityId
): PermissionDefinitionGroup =>
  new Map(
    Array.from(filterChangedPermissionsRecord(groups, permissionList, entityId).values()).flatMap((definitions) =>
      Array.from(definitions.entries())
    )
  )
